/** Tron 注入：统一走 TronLink 协议（tronLink / tronWeb），兼容 TP 等内置浏览器 */

import { base58de } from './tron.mjs'
import { pickEip6963ProviderForWallet } from './injectProbe.mjs'
import {
  initTronInjectProbe,
  waitForTronInjectProbe,
  resolveTronInjectForWallet,
  providerMatchesTronWalletKey,
  isTronWalletKeyInjectAvailable,
  requestTronInjectProbeRefresh,
  inferTronWalletKeyFromTip6963Provider,
} from './tronInjectProbe.mjs'
import { isMetaMaskTronProvider } from './metamaskTron.mjs'

export {
  initTronInjectProbe,
  waitForTronInjectProbe,
  resolveTronInjectForWallet,
  providerMatchesTronWalletKey,
  isTronWalletKeyInjectAvailable,
  requestTronInjectProbeRefresh,
} from './tronInjectProbe.mjs'

export const TRON_CHAIN_ID = 95500
export const DEFAULT_TRON_INJECT_KEY = 'tronLink'
const DEFAULT_READY_MS = 10000
const DEFAULT_REQUEST_MS = 18000
const DEFAULT_ADDRESS_MS = 8000

export const TRON_WALLET_KEYS = new Set(['tronLink', 'tokenPocket', 'bitget', 'ave', 'binance', 'okx'])

export function getScope(scope) {
  if (scope) return scope
  if (typeof globalThis !== 'undefined') return globalThis
  return {}
}

export function isTronPageContext(scope) {
  const loc = getScope(scope).location
  return !!loc && String(loc.search || '').includes('fromTron')
}

export function isTronChainId(chainId) {
  return Number(chainId) === TRON_CHAIN_ID
}

export function isTronContext(chainId, scope) {
  return isTronChainId(chainId) || isTronPageContext(scope)
}

/**
 * 钱包内置浏览器（TP / TronLink App 等）打开本站：通常无 ?fromTron，但有 tronLink 注入。
 * 桌面浏览器仅装 TronLink 扩展不算（避免误切 Tron 模式）。
 */
export function isTronDappBrowser(scope) {
  const g = getScope(scope)
  if (!isTronInjectAvailable(g)) return false
  try {
    const ua = String(g.navigator?.userAgent || '')
    if (/TokenPocket/i.test(ua)) return true
    if (/TronLink/i.test(ua)) return true
    if (/Bitget|BitKeep/i.test(ua)) return true
    if (/BNC\/|Binance/i.test(ua)) return true
    if (/OKApp|OKX/i.test(ua)) return true
    if (/AVE|Ave|ave\.ai/i.test(ua)) return true
    if (g.document?.documentElement?.classList?.contains('hs-tokenpocket')) return true
  } catch (_e) {
    void _e
  }
  return false
}

/** UI / 连接流程是否应按 Tron 处理：仅 URL ?fromTron 或当前链为 Tron，不因 TP 内置 tronWeb 误判 */
export function isTronUiContext(chainId, scope) {
  return isTronContext(chainId, scope)
}

export function resolveTronLink(scope) {
  const g = getScope(scope)
  try {
    return g.tronLink || null
  } catch (_e) {
    return null
  }
}

/** TronLink 协议注入（TP / Bitget / OKX / Binance 等扩展或内置浏览器） */
export function resolveTronWeb(scope) {
  const g = getScope(scope)
  try {
    const link = resolveTronLink(g)
    return (
      g.tronWeb ||
      link?.tronWeb ||
      g.tokenpocket?.tronWeb ||
      g.tokenpocket?.tron?.tronWeb ||
      g.tp?.tronWeb ||
      g.tp?.tron?.tronWeb ||
      g.bitkeep?.tronWeb ||
      g.bitkeep?.tron?.tronWeb ||
      g.bitkeep?.tronLink?.tronWeb ||
      g.okxwallet?.tronWeb ||
      g.okxwallet?.tron?.tronWeb ||
      g.okxwallet?.tronLink?.tronWeb ||
      g.BinanceChain?.tronWeb ||
      g.binancew3w?.tronWeb ||
      g.binancew3w?.tron?.tronWeb ||
      null
    )
  } catch (_e) {
    return null
  }
}

/** 是否可发起 TronLink 标准连接 */
export function isTronInjectAvailable(scope) {
  const g = getScope(scope)
  return !!(resolveTronWeb(g) || resolveTronLink(g))
}

function walletInAppBrowser(key, ua) {
  const u = String(ua || '')
  if (key === 'tokenPocket') return /TokenPocket/i.test(u)
  if (key === 'bitget') return /Bitget|BitKeep/i.test(u)
  if (key === 'ave') return /AVE|Ave|ave\.ai/i.test(u)
  if (key === 'binance') return /BNC\/|Binance/i.test(u)
  if (key === 'okx') return /OKApp|OKX/i.test(u)
  return false
}

function collectEvmInjectProviders(scope) {
  const g = getScope(scope)
  const out = []
  const push = (p) => {
    if (p && typeof p.request === 'function' && !out.includes(p)) out.push(p)
  }
  try {
    const eth = g.ethereum
    if (Array.isArray(eth?.providers)) eth.providers.forEach(push)
    push(eth)
    push(g.bitkeep?.ethereum)
    push(g.okxwallet)
    push(g.BinanceChain?.ethereum)
    push(g.binancew3w?.ethereum)
    push(g.avewallet?.ethereum)
    push(g.aveWallet?.ethereum)
    push(g.ave?.ethereum)
  } catch (_e) {
    void _e
  }
  return out
}

function hasWalletBrowserExtension(key, scope) {
  if (pickEip6963ProviderForWallet(key)) return true
  const providers = collectEvmInjectProviders(scope)
  const checks = {
    tokenPocket: (p) => !!p.isTokenPocket,
    bitget: (p) => !!(p.isBitKeep || p.isBitgetWallet),
    ave: (p) => !!(p.isAveWallet || p.isAVEWallet || p.isAVE || p.isAve),
    binance: (p) => !!(p.isBinance || p.isBinanceWallet),
    okx: (p) => !!(p.isOkxWallet || p.isOKExWallet),
  }
  const fn = checks[key]
  return fn ? providers.some(fn) : false
}

/**
 * 所选 Tron 钱包是否可走 TronLink 协议注入：
 * - TronLink：有 tronWeb 即可
 * - 其它：对应 App 内置浏览器，或桌面已装该钱包扩展且暴露 tronWeb
 */
export function isTronInjectWalletAvailable(wallet, scope) {
  const key = String(wallet?.key || wallet || '').trim()
  if (key === 'metamask') return false
  if (key !== 'tronLink' && !TRON_WALLET_KEYS.has(key)) return false
  const g = getScope(scope)
  const ua = String(g.navigator?.userAgent || '')
  if (walletInAppBrowser(key, ua)) {
    return isTronInjectAvailable(scope) && !!resolveTronInjectForWallet(key, scope)?.tronWeb
  }
  return isTronWalletKeyInjectAvailable(key, scope)
}

/** 无 Tron 注入时桌面走 WalletConnect 扫码（保留导出供 UI 判断） */
export const TRON_WC_FALLBACK_KEYS = new Set()

/** UI 展示用：Tron 模式完整钱包列表 key */
export const TRON_UI_WALLET_KEYS = [
  'tronLink',
  'metamask',
  'bitget',
  'ave',
  'tokenPocket',
  'binance',
  'okx',
  'walletConnect',
]

/** Tron 注入连接 key（TronLink 协议） */
export const TRON_INJECT_KEYS = new Set(['tronLink', 'tokenPocket', 'bitget', 'ave', 'binance', 'okx'])

export function resolveTronInjectSessionKey(preferredKey = '') {
  const key = String(preferredKey || '').trim()
  if (key === 'metamask' || key === 'walletConnect') return key
  if (key && (TRON_WALLET_KEYS.has(key) || key === 'tronLink')) return key
  try {
    const ua = typeof navigator !== 'undefined' ? navigator.userAgent || '' : ''
    if (/TokenPocket/i.test(ua)) return 'tokenPocket'
    if (/Bitget|BitKeep/i.test(ua)) return 'bitget'
    if (/BNC\/|Binance/i.test(ua)) return 'binance'
    if (/OKApp|OKX/i.test(ua)) return 'okx'
    if (/AVE|Ave/i.test(ua)) return 'ave'
  } catch (_e) {
    void _e
  }
  return DEFAULT_TRON_INJECT_KEY
}

/** 从 TRON provider 推断钱包 key（MetaMask Tron / TronLink / TP 等） */
export function inferTronWalletKeyFromProvider(provider) {
  if (!provider) return ''
  if (isMetaMaskTronProvider(provider)) return 'metamask'

  const fromTip = inferTronWalletKeyFromTip6963Provider(provider)
  if (fromTip) return fromTip

  const matches = []
  for (const key of TRON_INJECT_KEYS) {
    if (providerMatchesTronWalletKey(provider, key)) matches.push(key)
  }
  if (!matches.length) return ''
  if (matches.length === 1) return matches[0]
  // 多扩展共用 window.tronWeb 时，tronLink 仅为协议占位，优先具体钱包
  const nonLink = matches.filter((k) => k !== 'tronLink')
  return nonLink[0] || matches[0]
}

/** UI 会话 key：用户点选 / 已存 key 优先，再 provider 推断 */
export function resolveTronWalletSessionKey(preferredKey = '', provider = null) {
  const pref = String(preferredKey || '').trim()
  if (pref === 'metamask' || pref === 'walletConnect') return pref
  if (pref && (TRON_INJECT_KEYS.has(pref) || pref === 'tronLink')) {
    if (!provider || providerMatchesTronWalletKey(provider, pref)) {
      return pref
    }
  }
  const fromProvider = inferTronWalletKeyFromProvider(provider)
  if (fromProvider) return fromProvider
  return resolveTronInjectSessionKey(pref)
}

function sleepMs(ms) {
  return new Promise((r) => setTimeout(r, ms))
}

function withTimeout(promise, ms, code = 'TRON_TIMEOUT') {
  return Promise.race([
    promise,
    sleepMs(ms).then(() => {
      const err = new Error(code)
      err.code = code
      throw err
    }),
  ])
}

function normalizeTronHex(hex) {
  let raw = String(hex || '').replace(/^0x/i, '').trim()
  if (!raw) return ''
  if (raw.startsWith('41') && raw.length === 42) raw = raw.slice(2)
  if (raw.length !== 40) return ''
  return raw
}

function accountFromBase58(addr) {
  const s = String(addr || '').trim()
  if (!s.startsWith('T')) return ''
  try {
    const evm = base58de(s)
    return normalizeTronHex(evm)
  } catch (_e) {
    return ''
  }
}

/** 从 tronWeb / 授权结果提取内部 0x 账户（20 字节 hex，不含 41 前缀） */
export function extractTronAccountHex(tronWeb, requestResult) {
  let hex = normalizeTronHex(tronWeb?.defaultAddress?.hex)
  if (hex) return hex

  hex = accountFromBase58(tronWeb?.defaultAddress?.base58)
  if (hex) return hex

  if (Array.isArray(requestResult)) {
    for (const item of requestResult) {
      hex = accountFromBase58(item) || normalizeTronHex(item)
      if (hex) return hex
    }
  }

  if (requestResult && typeof requestResult === 'object') {
    hex =
      accountFromBase58(requestResult.address) ||
      normalizeTronHex(requestResult.address) ||
      accountFromBase58(requestResult.data?.address)
    if (hex) return hex
  }

  return ''
}

/** 已有授权时可静默读取，不弹窗 */
export function readTronAccountPassive(scope, walletKey = '') {
  const inject = walletKey
    ? resolveTronInjectForWallet(walletKey, scope)
    : { tronWeb: resolveTronWeb(scope) }
  const tronWeb = inject?.tronWeb
  if (!tronWeb) return null
  const hex = extractTronAccountHex(tronWeb, null)
  if (!hex) return null
  return {
    account: '0x' + hex,
    chainId: TRON_CHAIN_ID,
    provider: tronWeb,
  }
}

async function waitTronLinkReadyForInject(scope, inject, timeoutMs = DEFAULT_READY_MS) {
  const g = getScope(scope)
  const tronWeb = inject?.tronWeb
  const tronLink = inject?.tronLink || resolveTronLink(g)
  if (tronWeb && (tronWeb.defaultAddress?.hex || tronWeb.defaultAddress?.base58 || tronLink?.ready)) {
    return { tronLink, tronWeb }
  }

  return withTimeout(
    new Promise((resolve) => {
      let settled = false
      const finish = (tw) => {
        if (settled) return
        settled = true
        resolve({ tronLink: tronLink || resolveTronLink(g), tronWeb: tw || tronWeb || resolveTronWeb(g) })
      }

      const tryResolve = () => {
        const tw = inject?.tronWeb || resolveTronWeb(g)
        if (tw?.defaultAddress?.hex || tw?.defaultAddress?.base58) {
          finish(tw)
          return true
        }
        if (tronLink?.ready && tw) {
          finish(tw)
          return true
        }
        return false
      }

      if (tryResolve()) return

      const onInit = () => { tryResolve() || finish(inject?.tronWeb || resolveTronWeb(g)) }
      g.addEventListener?.('tronLink#initialized', onInit, { once: true })

      const poll = setInterval(() => {
        if (tryResolve()) clearInterval(poll)
      }, 150)

      setTimeout(() => {
        clearInterval(poll)
        if (!settled) finish(inject?.tronWeb || resolveTronWeb(g))
      }, timeoutMs - 50)
    }),
    timeoutMs,
    'TRON_LINK_READY_TIMEOUT',
  )
}

async function requestTronAccounts(scope, options = {}) {
  const g = getScope(scope)
  const inject = options.inject || null
  const tronLink = inject?.tronLink || resolveTronLink(g)
  let tronWeb = inject?.tronWeb || null

  await waitTronLinkReadyForInject(scope, inject, options.readyTimeoutMs)

  if (!tronWeb) tronWeb = inject?.tronWeb || resolveTronWeb(g)

  const runners = []
  if (tronLink?.request) {
    runners.push(() => tronLink.request({ method: 'tron_requestAccounts' }))
  }
  if (tronWeb?.request) {
    runners.push(() => tronWeb.request({ method: 'tron_requestAccounts' }))
  }

  let lastResult = null
  const timeoutMs = options.timeoutMs ?? DEFAULT_REQUEST_MS
  for (const run of runners) {
    try {
      lastResult = await withTimeout(run(), timeoutMs, 'TRON_REQUEST_TIMEOUT')
      tronWeb = inject?.tronWeb || tronWeb || resolveTronWeb(g)
      const hex = extractTronAccountHex(tronWeb, lastResult)
      if (hex) return { result: lastResult, tronWeb, hex }
    } catch (e) {
      console.warn('[hieswap] tron_requestAccounts', e?.code || e?.message || e)
    }
  }

  tronWeb = inject?.tronWeb || tronWeb || resolveTronWeb(g)
  const hex = extractTronAccountHex(tronWeb, lastResult)
  return { result: lastResult, tronWeb, hex: hex || '' }
}

async function waitTronAccountAfterRequest(tronWeb, requestResult, options = {}) {
  let hex = extractTronAccountHex(tronWeb, requestResult)
  if (hex) return hex

  const timeoutMs = options.timeoutMs ?? DEFAULT_ADDRESS_MS
  const intervalMs = options.intervalMs ?? 120
  const deadline = Date.now() + timeoutMs
  while (Date.now() < deadline) {
    hex = extractTronAccountHex(tronWeb, requestResult)
    if (hex) return hex
    await sleepMs(intervalMs)
  }
  return ''
}

/**
 * 连接 Tron 账户（TronLink 标准：ready → tron_requestAccounts → 读地址）
 * @param {object} [options]
 * @param {boolean} [options.passive] 仅读取已授权账户，不发起 request
 */
export async function connectTronAccount(options = {}) {
  const scope = getScope(options.scope)
  const walletKey = String(options.walletKey || '').trim()
  const tronOnly = !!options.tronOnly

  if (walletKey) {
    await waitForTronInjectProbe({ timeoutMs: 900 })
  }

  const inject = walletKey ? resolveTronInjectForWallet(walletKey, scope) : null
  if (walletKey && tronOnly && !inject?.tronWeb) {
    const err = new Error('NO_TRON_PROVIDER')
    err.code = 'NO_TRON_PROVIDER'
    throw err
  }

  if (options.passive) {
    const passive = readTronAccountPassive(scope, walletKey)
    if (!passive) {
      const err = new Error('NO_TRON_ACCOUNT')
      err.code = 'NO_TRON_ACCOUNT'
      throw err
    }
    return passive
  }

  const { tronWeb, result, hex: quickHex } = await requestTronAccounts(scope, {
    inject,
    timeoutMs: options.requestTimeoutMs,
    readyTimeoutMs: options.readyTimeoutMs ?? DEFAULT_READY_MS,
  })
  if (!tronWeb) {
    const err = new Error('NO_TRON_PROVIDER')
    err.code = 'NO_TRON_PROVIDER'
    throw err
  }

  if (walletKey && !providerMatchesTronWalletKey(tronWeb, walletKey)) {
    const err = new Error('TRON_WALLET_MISMATCH')
    err.code = 'TRON_WALLET_MISMATCH'
    throw err
  }

  let hex = quickHex || await waitTronAccountAfterRequest(tronWeb, result, options)
  if (!hex) {
    const err = new Error('NO_TRON_ACCOUNT')
    err.code = 'NO_TRON_ACCOUNT'
    throw err
  }

  return {
    account: '0x' + hex,
    chainId: TRON_CHAIN_ID,
    provider: tronWeb,
  }
}

const tronListenerState = {
  messageHandler: null,
  initHandler: null,
  tronLinkAccountsHandler: null,
  tronLinkDisconnectHandler: null,
  scope: null,
}

export function isTronInjectProvider(provider, scope) {
  if (!provider) return false
  if (isMetaMaskTronProvider(provider)) return true
  const g = getScope(scope)
  const tronWeb = resolveTronWeb(g)
  if (provider === tronWeb) return true
  const link = resolveTronLink(g)
  if (link?.tronWeb && provider === link.tronWeb) return true
  for (const key of TRON_INJECT_KEYS) {
    const inject = resolveTronInjectForWallet(key, g)
    if (inject?.tronWeb === provider) return true
  }
  return false
}

/** TronLink 账户变更与断开（不整页刷新） */
export function attachTronWalletListeners(callbacks = {}, scope) {
  detachTronWalletListeners()
  const g = getScope(scope)
  tronListenerState.scope = g
  const { onAccountsChanged, onDisconnect } = callbacks

  const emitAccountsChanged = () => {
    const passive = readTronAccountPassive(g)
    if (passive?.account) {
      onAccountsChanged?.(passive.account, passive)
    } else {
      onDisconnect?.()
    }
  }

  tronListenerState.messageHandler = (e) => {
    const msg = e?.data?.message
    if (!msg?.action) return
    const action = String(msg.action)
    if (
      action === 'accountsChanged' ||
      action === 'setAccount' ||
      action === 'connect' ||
      action === 'acceptWeb'
    ) {
      emitAccountsChanged()
    } else if (action === 'disconnect' || action === 'disconnectWeb' || action === 'rejectWeb') {
      onDisconnect?.()
    }
  }
  g.addEventListener?.('message', tronListenerState.messageHandler)

  tronListenerState.initHandler = () => emitAccountsChanged()
  g.addEventListener?.('tronLink#initialized', tronListenerState.initHandler)

  const tronLink = resolveTronLink(g)
  if (tronLink && typeof tronLink.on === 'function') {
    tronListenerState.tronLinkAccountsHandler = () => emitAccountsChanged()
    tronListenerState.tronLinkDisconnectHandler = () => onDisconnect?.()
    try {
      tronLink.on('accountsChanged', tronListenerState.tronLinkAccountsHandler)
      tronLink.on('disconnect', tronListenerState.tronLinkDisconnectHandler)
    } catch (_e) {
      void _e
    }
  }
}

export function detachTronWalletListeners() {
  const g = tronListenerState.scope || getScope()
  if (tronListenerState.messageHandler) {
    g.removeEventListener?.('message', tronListenerState.messageHandler)
  }
  if (tronListenerState.initHandler) {
    g.removeEventListener?.('tronLink#initialized', tronListenerState.initHandler)
  }
  const tronLink = resolveTronLink(g)
  if (tronLink) {
    const off = tronLink.removeListener || tronLink.off
    if (typeof off === 'function') {
      if (tronListenerState.tronLinkAccountsHandler) {
        try { off.call(tronLink, 'accountsChanged', tronListenerState.tronLinkAccountsHandler) } catch (_e) { void _e }
      }
      if (tronListenerState.tronLinkDisconnectHandler) {
        try { off.call(tronLink, 'disconnect', tronListenerState.tronLinkDisconnectHandler) } catch (_e) { void _e }
      }
    }
  }
  tronListenerState.messageHandler = null
  tronListenerState.initHandler = null
  tronListenerState.tronLinkAccountsHandler = null
  tronListenerState.tronLinkDisconnectHandler = null
  tronListenerState.scope = null
}
